import { useMemo, useRef } from 'react'
import get from 'lodash.get'
import { buildFromHierarchy } from './buildFromHierarchy'
import { buildFromTree } from './buildFromTree'

function buildNodesFromStructured (rawNodes, accessors) {
  if (!Array.isArray(rawNodes)) return []

  return rawNodes.map((item, index) => {
    const id = String(get(item, accessors.nodeId) ?? index)
    return {
      id,
      type: 'flowGraphCard',
      position: { x: 0, y: 0 },
      data: {
        nameValue: get(item, accessors.nodeName),
        descriptionValue: get(item, accessors.nodeDescription),
        image: get(item, accessors.nodeImage),
        raw: item
      }
    }
  })
}

function buildEdgesFromStructured (rawEdges, accessors) {
  if (!Array.isArray(rawEdges)) return []

  return rawEdges.reduce((edges, item, index) => {
    const source = get(item, accessors.edgeSource)
    const target = get(item, accessors.edgeTarget)
    if (source == null || target == null) return edges

    const label = get(item, accessors.edgeLabel)
    edges.push({
      id: `e-${source}-${target}-${index}`,
      source: String(source),
      target: String(target),
      ...(label ? { label: String(label) } : {})
    })
    return edges
  }, [])
}

function buildFromRelational (items, accessors) {
  if (!Array.isArray(items)) return { nodes: [], edges: [] }

  const nodes = items.map((item, index) => {
    const id = String(get(item, accessors.nodeId) ?? index)
    return {
      id,
      type: 'flowGraphCard',
      position: { x: 0, y: 0 },
      data: {
        nameValue: get(item, accessors.nodeName),
        descriptionValue: get(item, accessors.nodeDescription),
        image: get(item, accessors.nodeImage),
        raw: item
      }
    }
  })

  const edges = []
  items.forEach((item, index) => {
    const sourceId = String(get(item, accessors.nodeId) ?? index)
    const parentRef = get(item, accessors.edgeSource) ?? get(item, accessors.edgeTarget)
    if (parentRef != null) {
      edges.push({
        id: `e-${parentRef}-${sourceId}`,
        source: String(parentRef),
        target: sourceId
      })
    }
  })

  return { nodes, edges }
}

function useStableJSON (value) {
  const ref = useRef(value)
  const serialized = JSON.stringify(value)
  const prevSerialized = useRef(serialized)

  if (serialized !== prevSerialized.current) {
    ref.current = value
    prevSerialized.current = serialized
  }

  return ref.current
}

export default function useFlowGraphData (data, {
  dataMode,
  dataAccessor,
  levels: _levels,
  edgeLabels: _edgeLabels,
  groupingAccessor = 'grouping',
  childrenAccessor = 'data',
  nodesAccessor = 'nodes',
  edgesAccessor = 'edges',
  nodeIdAccessor = 'id',
  nodeNameAccessor = 'name',
  nodeDescriptionAccessor,
  nodeImageAccessor,
  edgeSourceAccessor = 'source',
  edgeTargetAccessor = 'target',
  edgeLabelAccessor
} = {}) {
  const levels = useStableJSON(_levels)
  const edgeLabels = useStableJSON(_edgeLabels)

  return useMemo(() => {
    if (!data) return { nodes: [], edges: [] }

    if (dataMode === 'flat' && Array.isArray(levels)) {
      const items = dataAccessor ? get(data, dataAccessor) : data
      if (!Array.isArray(items)) return { nodes: [], edges: [] }
      return buildFromHierarchy(items, levels, edgeLabels)
    }

    if (dataMode === 'tree' && Array.isArray(levels)) {
      const items = dataAccessor ? get(data, dataAccessor) : data
      if (!Array.isArray(items)) return { nodes: [], edges: [] }
      return buildFromTree(items, levels, edgeLabels, { childrenAccessor, groupingAccessor })
    }

    const accessors = {
      nodeId: nodeIdAccessor,
      nodeName: nodeNameAccessor,
      nodeDescription: nodeDescriptionAccessor,
      nodeImage: nodeImageAccessor,
      edgeSource: edgeSourceAccessor,
      edgeTarget: edgeTargetAccessor,
      edgeLabel: edgeLabelAccessor
    }

    const rawNodes = get(data, nodesAccessor)

    if (Array.isArray(rawNodes)) {
      const rawEdges = get(data, edgesAccessor)
      return {
        nodes: buildNodesFromStructured(rawNodes, accessors),
        edges: buildEdgesFromStructured(rawEdges || [], accessors)
      }
    }

    const flatItems = Array.isArray(data) ? data : get(data, 'data')
    if (Array.isArray(flatItems)) {
      return buildFromRelational(flatItems, accessors)
    }

    return { nodes: [], edges: [] }
  }, [
    data, dataMode, dataAccessor, levels, edgeLabels,
    groupingAccessor, childrenAccessor,
    nodesAccessor, edgesAccessor,
    nodeIdAccessor, nodeNameAccessor, nodeDescriptionAccessor, nodeImageAccessor,
    edgeSourceAccessor, edgeTargetAccessor, edgeLabelAccessor
  ])
}
